import * as React from 'react';
import { Link } from 'react-router-dom';

import { LeadStatus, leadStatusLabels } from '@/features/leads/schemas';
import type { Lead } from '@/features/leads/types';
import { Badge } from '@/shared/ui/badge';

type BadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'destructive';

const STATUS_VARIANT: Record<number, BadgeVariant> = {
  [LeadStatus.New]: 'secondary',
  [LeadStatus.Open]: 'default',
  [LeadStatus.Approved]: 'success',
  [LeadStatus.Converted]: 'success',
  [LeadStatus.Cancelled]: 'destructive',
  [LeadStatus.Churned]: 'warning',
};

function initials(title: string): string {
  const parts = title.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * AdminRecentLeads — compact recent-leads list for the command center's
 * quick-data column. Reads the live `/Lead` list (most recent first).
 */
export function AdminRecentLeads({ leads }: { leads: Lead[] }) {
  if (leads.length === 0) {
    return (
      <p className="tw-py-4 tw-text-sm tw-text-muted-foreground">No recent leads.</p>
    );
  }

  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0">
      {leads.map((lead) => (
        <li
          key={lead.id}
          className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-border tw-py-2.5 last:tw-border-0"
        >
          <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-xs tw-font-bold tw-text-primary">
            {initials(lead.title)}
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <Link
              to={`/leads/view/${lead.id}`}
              className="tw-block tw-truncate tw-text-[13px] tw-font-semibold tw-text-foreground hover:tw-text-primary"
            >
              {lead.title}
            </Link>
            <p className="tw-m-0 tw-truncate tw-text-[11.5px] tw-text-muted-foreground">
              {lead.contactPerson || lead.contactEmail || '—'}
            </p>
          </div>
          <Badge variant={STATUS_VARIANT[lead.leadStatus] ?? 'secondary'}>
            {leadStatusLabels[lead.leadStatus] ?? 'Unknown'}
          </Badge>
        </li>
      ))}
    </ul>
  );
}
